// ----- phase-draggable-resizable-editor-modal — modal keyboard a11y -----
//
// Escape-to-close + a lightweight focus trap for heavyweight editor modals.
// The Company Profile editor had NEITHER (ConfirmModal already handles its
// own Escape); making the editor draggable/resizable must not leave it
// mouse-only, so this ships alongside that behaviour as its own small,
// reusable hook.
//
//   * Escape closes (unless `disabled` — e.g. while a save is in flight,
//     mirroring ConfirmModal's busy guard).
//   * Tab / Shift+Tab wrap within the modal, so focus cannot escape to the
//     page behind the overlay.
//   * On mount, focus moves into the modal (the container itself via
//     tabIndex=-1) so keyboard users start inside the trap.
//
// Restores focus to the connected opener on close. The shared dialog shell
// provides dialog semantics and the pointer-blocking overlay.

import { useCallback, useEffect, useRef, type KeyboardEvent } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export interface ModalA11yOptions {
  readonly onClose: () => void;
  /** Suppress Escape-to-close (e.g. while saving). */
  readonly disabled?: boolean;
}

export interface ModalA11yProps {
  readonly ref: (el: HTMLElement | null) => void;
  readonly onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  readonly tabIndex: number;
}

/** Spread the returned props onto the modal's window element. */
export function useModalA11y(options: ModalA11yOptions): ModalA11yProps {
  const elRef = useRef<HTMLElement | null>(null);
  const optsRef = useRef(options);
  optsRef.current = options;

  const ref = useCallback((el: HTMLElement | null): void => {
    elRef.current = el;
  }, []);

  // Initial focus: the window itself, so Tab starts cycling inside the trap
  // without stealing focus from a field the consumer autoFocuses.
  useEffect(() => {
    const opener = document.activeElement;
    const el = elRef.current;
    if (el && !el.contains(document.activeElement)) {
      el.focus({ preventScroll: true });
    }
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>): void => {
      if (e.key === 'Escape') {
        if (!optsRef.current.disabled) {
          e.stopPropagation();
          optsRef.current.onClose();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const el = elRef.current;
      if (!el) return;
      // No offsetParent visibility filter: jsdom always reports null, and
      // this modal renders no hidden focusables (collapsed sections are
      // conditionally rendered, not display:none).
      const focusables = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (n) => n.getAttribute('aria-hidden') !== 'true',
      );
      if (focusables.length === 0) { e.preventDefault(); el.focus(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        // Wrap backward off the first focusable (or the container itself).
        if (active === first || active === el) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        // Wrap forward off the last focusable.
        e.preventDefault();
        first.focus();
      }
    },
    [],
  );

  return { ref, onKeyDown, tabIndex: -1 };
}
